import React, { useEffect, useState } from 'react';
import Zana, { ZanaState } from './Zana';
import { isBriefingsConfigured, zanaSelfTest, ZanaDiagnosis, fetchZanaBriefing, runZanaFleetRead, briefingAge, asList } from '../services/briefingsApi';
import { ZanaIntent, ZanaIntentExecutor } from '../services/zanaActions';
import ZanaActionChips from './ZanaActionChips';
import { customToast } from '../utils/customToast';

/* ============================================================================
   ZanaModeTab — the premium "Zana Mode" analytics tab. Reads aiBriefings/{orgCode}
   and renders the fleet-health briefing hero, day-0 missions, role lenses,
   4-KPI row, fuel pattern checks (tap-to-explain), ETA, maintenance, coaching,
   recommendations, and the "included in your plan" band.
   ========================================================================== */

interface Coverage {
  gps?: { level?: string; source?: string; learnedRoutes?: number; totalRoutes?: number; trips?: number };
  fuel?: { level?: string; source?: string; baselinesSufficient?: number; busCount?: number; events?: number };
  fees?: { connected?: boolean };
  drivers?: { level?: string; ratedCount?: number };
  incidents?: { count?: number };
}

interface Briefing {
  source?: 'ai' | 'deterministic' | 'learning' | 'configuring';
  configureStage?: string;
  configureStageIndex?: number;
  configureTotalStages?: number;
  mascotState?: ZanaState;
  headline?: string;
  summary?: string;
  healthScore?: number | null;
  learningDaysLeft?: number;
  learningProgress?: number;
  learningBreakdown?: { inventory?: number; roster?: number; telemetry?: number; routes?: number; fuelBaselines?: number; drivers?: number; trips?: number };
  learningGaps?: string[];
  coverage?: Coverage;
  learnedStats?: {
    buses?: number; children?: number; drivers?: number; parents?: number;
    busesWithTelemetry?: number; routes?: number; learnedRoutes?: number; trips?: number; ratedDrivers?: number;
  };
  chips?: Array<{ kind: 'urgent' | 'coaching' | 'savings'; label: string }>;
  kpis?: {
    fleetHealth?: { value: number; delta: number };
    savingsFoundKES?: number;
    fuelAnomalies?: { count: number; severity: 'high' | 'amber' | 'ok'; note: string };
    predictedOnTimePct?: number;
  };
  fuelAnomalies?: Array<{
    vehicle: string; saw: string; unaccounted: string; estLossKES: number; severity: 'high' | 'review' | 'low';
    explainer?: { why?: string; basedOn?: string; check?: string };
  }>;
  etaPredictions?: Array<{ route: string; bus: string; window: string; confidence: 'high' | 'medium' | 'low' }>;
  maintenance?: Array<{ vehicle: string; due: string; status: 'red' | 'amber' | 'green' }>;
  coaching?: Array<{ driverLabel: string; stars: number; note: string }>;
  recommendations?: Array<{ rank: number; text: string; savingsKES: number }>;
  missions?: Array<{
    id: string; pillar: 'safety' | 'money' | 'parents' | 'setup'; title: string; why: string; nextStep: string;
    actionId?: string; params?: Record<string, unknown>; savingsKES?: number;
  }>;
  roleLenses?: {
    accountant?: RoleLens; fleet?: RoleLens; head?: RoleLens;
  };
  generatedAtMs?: number;
  /* Why this briefing is rule-based, when it is. Written by the backend so the
     card can distinguish "deep analysis is switched off" from "it was called,
     billed, and timed out" — which looked identical until 2026-08-25. */
  aiFallback?: { reason?: string; detail?: string; note?: string; atMs?: number };
}

interface RoleLens {
  label?: string; headline?: string; bullets?: string[]; nextStep?: string;
}

type LensKey = 'accountant' | 'fleet' | 'head';

const PURPLE = '#6f4ff0';
const kes = (n?: number) => `Ksh ${Number(n || 0).toLocaleString()}`;
const healthPct = (n?: number | null) => (n == null ? '—' : `${Math.round(n)}%`);
const asPctSummary = (s?: string) => (s || '').replace(/\b(\d{1,3})\/100\b/g, '$1%');

const SEV_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
  high: { bg: '#fdecec', fg: '#c0362c', label: 'High' },
  review: { bg: '#fef3e2', fg: '#b06a12', label: 'Review' },
  low: { bg: '#eef7ee', fg: '#2f7d32', label: 'Low' },
};
const CONF_STYLE: Record<string, { bg: string; fg: string }> = {
  high: { bg: '#e8f6ee', fg: '#16a34a' },
  medium: { bg: '#eef3ff', fg: '#2563eb' },
  low: { bg: '#f4f0ff', fg: '#6f4ff0' },
};
const DOT: Record<string, string> = { red: '#dc2626', amber: '#d97706', green: '#16a34a' };

export default function ZanaModeTab({
  orgCode,
  onExecuteIntent,
}: {
  orgCode: string | null;
  onExecuteIntent?: ZanaIntentExecutor;
}) {
  const [brief, setBrief] = useState<Briefing | null>(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [lens, setLens] = useState<LensKey>('head');
  const [openAnomaly, setOpenAnomaly] = useState<number | null>(null);

  useEffect(() => {
    if (!orgCode) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    // Read via the backend, not client Firestore — rules block the browser's
    // aiBriefings read for some org admins, which is what made this tab show a
    // permanent "hasn't read your fleet" even after a full briefing was written.
    fetchZanaBriefing(orgCode)
      .then((b) => { if (alive) { setBrief(b as Briefing | null); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [orgCode]);

  async function handleRegenerate() {
    if (!orgCode) return;
    setRegenerating(true);
    setConfigError(null);
    // No briefing yet (or still learning/configuring) → staged configure;
    // otherwise refresh in place. Poll runs alongside HTTP so a timed-out
    // response does not re-enable the button while the server is still working.
    const mode = (!brief || brief.source === 'learning' || brief.source === 'configuring')
      ? 'configure'
      : 'regenerate';
    try {
      const result = await runZanaFleetRead(orgCode, {
        mode,
        onBrief: (b) => setBrief(b as Briefing | null),
      });
      if (!result.ok) {
        setConfigError(result.message || 'Could not finish reading the fleet. Please try again.');
      } else if (result.result?.state === 'cached') {
        /* A press the server deliberately declined, because a real AI briefing
           already covers this exact fleet state. Legitimate — but silence made
           it indistinguishable from a broken button, which is precisely how
           "I pressed Regenerate and nothing happened" kept meaning two
           different things. Say which one it is. */
        customToast.show('Nothing has changed since Zana\'s last read — your briefing is already current.', 'success');
      }
    } finally {
      setRegenerating(false);
    }
  }

  // Learning window / configure run / no briefing yet.
  const configuring = brief?.source === 'configuring';
  const learning = brief?.source === 'learning' || configuring || (!brief && !loading);
  const stats = brief?.learnedStats || {};
  const gaps = brief?.learningGaps || [];
  const knownPct = Math.round((brief?.learningProgress ?? 0) * 100);
  // Only a real briefing has an age worth showing; "learning"/"configuring" is
  // a live state, not a stale document.
  const age = learning ? null : briefingAge(brief);
  const heroState: ZanaState = (regenerating || configuring) ? 'scanning' : (brief?.mascotState as ZanaState) || (learning ? 'thinking' : 'online');

  return (
    <div className="zana-tab">
      {/* ── Briefing hero ─────────────────────────────────────────── */}
      <div className="zana-hero">
        <div className="zana-hero-glow" />
        <div className="zana-hero-robot"><Zana state={heroState} size={128} ground animated /></div>
        <div className="zana-hero-body">
          <div className="zana-hero-eyebrow">
            <span className="zana-hero-badge">ZANA</span>
            Zana Fleet Health Briefing
            <span className="zana-hero-meta">
              {age && (
                <span
                  className={`zana-age${age.stale ? ' zana-age-stale' : ''}`}
                  title={brief?.generatedAtMs ? `Briefing written ${new Date(brief.generatedAtMs).toLocaleString()}` : undefined}
                >
                  {age.stale ? `⚠ Out of date · ${age.label}` : `Updated ${age.label}`}
                </span>
              )}
              {brief?.source === 'ai' && <span className="zana-fresh">● Fresh</span>}
              {brief?.source === 'deterministic' && (
                <span
                  className="zana-fresh zana-fresh-rule"
                  title={brief?.aiFallback?.detail
                    ? `Deep analysis did not run: ${brief.aiFallback.detail}`
                    : undefined}
                >
                  ● Rule-based
                </span>
              )}
            </span>
          </div>
          {age?.stale && (
            <p className="zana-stale-note">
              These numbers are from {age.label} — Zana's daily refresh has not run since. Press Regenerate; if it
              fails it will now tell you why.
            </p>
          )}
          {/* "Rule-based" used to mean three different things and name none of
              them: switched off, over the spend cap, or the deep call was made,
              billed and timed out. Say which. */}
          {!learning && brief?.aiFallback?.note && (
            <p className="zana-fallback-note">{brief.aiFallback.note}</p>
          )}
          {loading ? (
            <div className="zana-hero-headline">Waking Zana…</div>
          ) : learning ? (
            <>
              <div className="zana-hero-headline">
                {configuring
                  ? (brief?.configureStage || 'Reading your fleet…')
                  : brief ? `Zana knows your fleet ${knownPct}% so far` : "Zana hasn't read your fleet yet"}
              </div>
              {configuring && (
                <p className="zana-hero-summary" style={{ opacity: 0.85 }}>
                  Step {brief?.configureStageIndex ?? 1} of {brief?.configureTotalStages ?? 4} — every step here is a
                  completed read of your own data, not an animation.
                </p>
              )}
              <p className="zana-hero-summary">
                {/* Lead with what she DOES know. The inventory is hers from day
                    one; only the run-time telemetry has to be earned. */}
                {stats.buses
                  ? `I've got your ${stats.buses} vehicle(s)${stats.drivers ? `, ${stats.drivers} driver(s)` : ''}${stats.children ? ` and ${stats.children} child(ren)` : ''} on file. `
                  : "I don't see any vehicles on your account yet. "}
                {gaps.length
                  ? `Still to come: ${gaps.slice(0, 3).join('; ')}.`
                  : 'Hit the button below for a read right now.'}
              </p>
              <div className="zana-learn">
                <div className="zana-learn-head">
                  <span>How much I know</span>
                  <span>{knownPct}%</span>
                </div>
                <div className="zana-learn-bar">
                  <div className="zana-learn-fill" style={{ width: `${Math.max(2, knownPct)}%` }} />
                </div>
                <div className="zana-learn-stats">
                  <span><b>{stats.buses ?? 0}</b> vehicles</span>
                  <span>watched <b>{stats.busesWithTelemetry ?? 0}</b>/{stats.buses ?? 0} run</span>
                  <span><b>{stats.learnedRoutes ?? 0}</b>/{stats.routes ?? 0} routes learned</span>
                  <span><b>{stats.trips ?? 0}</b> trips read</span>
                  <span><b>{stats.ratedDrivers ?? 0}</b>/{stats.drivers ?? 0} drivers rated</span>
                </div>
                {brief?.learningBreakdown && (
                  <div className="zana-learn-stats" style={{ opacity: 0.85 }}>
                    <span>Fleet on file <b>{brief.learningBreakdown.inventory ?? 0}%</b></span>
                    <span>Seen running <b>{brief.learningBreakdown.telemetry ?? 0}%</b></span>
                    <span>Routes <b>{brief.learningBreakdown.routes ?? 0}%</b></span>
                    <span>Fuel baselines <b>{brief.learningBreakdown.fuelBaselines ?? 0}%</b></span>
                    <span>Trip depth <b>{brief.learningBreakdown.trips ?? 0}%</b></span>
                  </div>
                )}
                <button className="zana-regen" onClick={handleRegenerate} disabled={regenerating}>
                  {regenerating || configuring ? 'Zana is reading your fleet…' : '⚙ Configure Zana — read my fleet now'}
                </button>
                {configError && (
                  <p className="zana-hero-summary" style={{ color: '#fca5a5', marginTop: 8 }}>{configError}</p>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="zana-hero-headline">{brief?.headline}</div>
              <p className="zana-hero-summary">{asPctSummary(brief?.summary)}</p>
              <div className="zana-chips">
                {asList<any>(brief?.chips).map((c, i) => (
                  <span key={i} className={`zana-chip zana-chip-${c.kind}`}>● {c.label}</span>
                ))}
                <button className="zana-regen" onClick={handleRegenerate} disabled={regenerating}>
                  {regenerating ? 'Zana is reading your fleet…' : '↻ Regenerate'}
                </button>
              </div>
              {configError && (
                <p className="zana-hero-summary" style={{ color: '#fca5a5', marginTop: 8 }}>{configError}</p>
              )}
              {brief?.coverage && (
                <div className="zana-learn-stats" style={{ marginTop: 10, opacity: 0.8 }}>
                  <span>Based on: routes <b>{brief.coverage.gps?.level === 'good' ? 'fully learned' : brief.coverage.gps?.level === 'partial' ? 'still learning' : 'no data yet'}</b></span>
                  <span>· fuel via <b>{brief.coverage.fuel?.source === 'sensor-addon' ? 'sensor add-on' : brief.coverage.fuel?.source === 'manual-logs' ? 'manual logs' : 'no data yet'}</b></span>
                  <span>· <b>{brief.coverage.drivers?.ratedCount ?? 0}</b> drivers rated</span>
                  {brief.coverage.fuel?.source === 'manual-logs' && <span>· a fuel-sensor add-on sharpens pattern checks</span>}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Day-0 / weekly missions — always visible once we have a briefing */}
      {!loading && !!brief && !configuring && (
        <MissionStrip missions={brief.missions} onExecuteIntent={onExecuteIntent} />
      )}

      {/* Role lenses — same data, three school voices */}
      {!learning && !loading && brief?.roleLenses && (
        <div className="zana-lenses">
          <div className="zana-lens-tabs">
            {([
              ['head', 'Headteacher'],
              ['accountant', 'Accountant'],
              ['fleet', 'Fleet'],
            ] as Array<[LensKey, string]>).map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`zana-lens-tab${lens === key ? ' active' : ''}`}
                onClick={() => setLens(key)}
              >
                {label}
              </button>
            ))}
          </div>
          {(() => {
            const L = brief.roleLenses?.[lens];
            if (!L) return null;
            return (
              <div className="zana-lens-body">
                <div className="zana-lens-headline">{L.headline}</div>
                <ul className="zana-lens-bullets">
                  {(L.bullets || []).map((b, i) => <li key={i}>{b}</li>)}
                </ul>
                {L.nextStep && <div className="zana-lens-next"><b>Do next —</b> {L.nextStep}</div>}
              </div>
            );
          })()}
        </div>
      )}

      {!learning && !loading && (
        <>
          {/* ── 4-KPI row ─────────────────────────────────────────── */}
          <div className="zana-kpis">
            <ZKpi
              label="Fleet health"
              value={healthPct(brief?.healthScore)}
              delta={brief?.kpis?.fleetHealth?.delta}
              note={brief?.healthScore == null ? 'vs last week'
                : `${brief.healthScore >= 85 ? 'Clean' : brief.healthScore >= 70 ? 'Steady' : 'Needs attention'} · vs last week`}
            />
            {/* "Savings found" said the opposite of what this number is.
                Gabriel, 2026-08-25: "it's not savings, it's actually where we
                are spending and don't need to." It counts money LEAVING the
                business that does not have to — fuel burnt that the trips
                cannot explain. Ksh 0 is the GOOD outcome, and "Savings found:
                Ksh 0" read like a failure. The stored field keeps its old name
                (it is in the model's JSON schema); only the words change. */}
            <ZKpi label="Wasted spend" value={kes(brief?.kpis?.savingsFoundKES)}
              note={(brief?.kpis?.savingsFoundKES || 0) > 0
                ? `per term · money you don't need to spend`
                : `nothing wasted · ${asList(brief?.recommendations).length} recs to act on`}
              accent />
            <ZKpi label="Fuel flags" value={String(brief?.kpis?.fuelAnomalies?.count ?? 0)}
              note={brief?.kpis?.fuelAnomalies?.note} warn={brief?.kpis?.fuelAnomalies?.severity === 'high'} />
            <ZKpi label="Predicted on-time" value={`${brief?.kpis?.predictedOnTimePct ?? '—'}%`} note="this week" good />
          </div>

          {/* ── Fuel pattern checks (tap row → explainer) ─────────── */}
          <ZCard title="Fuel pattern checks" note="each vehicle judged against its own history — tap a row for why"
            pill="AI · statistical" iconBg="#fdecec" iconFg="#c0362c">
            {asList(brief?.fuelAnomalies).length === 0 ? (
              <div className="zana-empty">
                {asList<any>(brief?.missions).some((m) => m.id === 'empty-fuel-mission' || m.pillar === 'money')
                  ? 'No fuel flags yet — see the mission cards above for how to unlock watching fuel.'
                  : 'No fuel flags — every vehicle is within its own normal range.'}
              </div>
            ) : (
              <table className="zana-table">
                <thead><tr><th>Vehicle</th><th>What Zana saw</th><th>Unaccounted</th><th>Est. loss</th><th>Severity</th></tr></thead>
                <tbody>
                  {asList<any>(brief?.fuelAnomalies).map((a, i) => (
                    <React.Fragment key={i}>
                      <tr
                        className={`zana-anomaly-row${openAnomaly === i ? ' open' : ''}`}
                        onClick={() => setOpenAnomaly(openAnomaly === i ? null : i)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="mono"><b>{a.vehicle}</b></td>
                        <td>{a.saw}</td>
                        <td className="mono">{a.unaccounted}</td>
                        <td className="mono">{a.estLossKES > 0 ? kes(a.estLossKES) : '—'}</td>
                        <td><SevPill sev={a.severity} /></td>
                      </tr>
                      {openAnomaly === i && a.explainer && (
                        <tr className="zana-explainer-row">
                          <td colSpan={5}>
                            <div className="zana-explainer">
                              <div><b>Why —</b> {a.explainer.why}</div>
                              <div><b>Based on —</b> {a.explainer.basedOn}</div>
                              <div><b>Check —</b> {a.explainer.check}</div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </ZCard>

          {/* ── ETA + maintenance ─────────────────────────────────── */}
          <div className="zana-grid-2">
            <ZCard title="Smart ETA predictions" note="parents see the same window on their live map"
              iconBg="#eef3ff" iconFg="#2563eb">
              {asList(brief?.etaPredictions).length === 0 ? (
                <div className="zana-empty">
                  Still learning your route timings — see the Parents mission above.
                </div>
              ) : (
                <table className="zana-table">
                  <thead><tr><th>Route</th><th>Bus</th><th>Predicted window</th><th>Confidence</th></tr></thead>
                  <tbody>
                    {asList<any>(brief?.etaPredictions).map((e, i) => (
                      <tr key={i}>
                        <td>{e.route}</td>
                        <td className="mono">{e.bus}</td>
                        <td className="mono">{e.window}</td>
                        <td><ConfPill conf={e.confidence} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </ZCard>

            <ZCard title="Predictive maintenance" note="based on each vehicle's own economy drift"
              iconBg="#fdf2e3" iconFg="#d97706">
              {asList(brief?.maintenance).length === 0 ? (
                <div className="zana-empty">Nothing due — the fleet is running smooth.</div>
              ) : (
                <div className="zana-maint">
                  {asList<any>(brief?.maintenance).map((m, i) => (
                    <div key={i} className="zana-maint-row">
                      <span className="zana-dot" style={{ background: DOT[m.status] }} />
                      <span className="mono zana-maint-veh">{m.vehicle}</span>
                      <span className="zana-maint-due">{m.due}</span>
                    </div>
                  ))}
                </div>
              )}
            </ZCard>
          </div>

          {/* ── Coaching + recommendations ────────────────────────── */}
          <div className="zana-grid-2">
            <ZCard title="Driver coaching" note="Zana's one specific note per driver"
              iconBg="#e8f6ee" iconFg="#16a34a">
              {asList(brief?.coaching).length === 0 ? (
                <div className="zana-empty">Drivers are all rating well.</div>
              ) : (
                <div className="zana-coach">
                  {asList<any>(brief?.coaching).map((c, i) => (
                    <div key={i} className="zana-coach-row">
                      <span className="zana-avatar">{c.driverLabel}</span>
                      <div>
                        <div className="zana-stars">{'★'.repeat(Math.round(c.stars))}<span className="zana-stars-off">{'★'.repeat(5 - Math.round(c.stars))}</span> <span className="zana-stars-num">{c.stars.toFixed(1)}</span></div>
                        <div className="zana-coach-note">{c.note}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ZCard>

            <ZCard title="Route & cost recommendations" note="ranked by what each one stops you spending — Ksh only from real logs"
              iconBg="#efe9ff" iconFg={PURPLE}>
              {asList(brief?.recommendations).length === 0 ? (
                <div className="zana-empty">No route changes recommended this week.</div>
              ) : (
                <div className="zana-recs">
                  {asList<any>(brief?.recommendations).map((r) => (
                    <div key={r.rank} className="zana-rec-row">
                      <span className="zana-rec-num">{r.rank}</span>
                      <span className="zana-rec-text">{r.text}</span>
                      {r.savingsKES > 0 && <span className="zana-rec-save">{kes(r.savingsKES)}</span>}
                    </div>
                  ))}
                </div>
              )}
            </ZCard>
          </div>
        </>
      )}

      {/* ── "included in your plan" info band (Decision 3) ────────── */}
      <div className="zana-included">
        <Zana state="happy" size={40} animated />
        <div>
          <b>Zana Mode is included in your plan.</b> No metering, no AI credits — Zana reads your fleet
          and writes this briefing automatically, cached for {12} hours. {!isBriefingsConfigured() && '(Regenerate needs the payments backend configured.)'}
        </div>
      </div>

      {/* ── AI connection diagnostic ──────────────────────────────── */}
      {orgCode && isBriefingsConfigured() && <AiConnectionTest orgCode={orgCode} />}
    </div>
  );
}

/* Fires one real Zana AI call through the backend and reports the truth — is
   the brain actually answering, or is Zana silently on her offline rules? */
function AiConnectionTest({ orgCode }: { orgCode: string }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ZanaDiagnosis | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function run() {
    setBusy(true); setErr(null); setResult(null);
    const res = await zanaSelfTest(orgCode);
    setBusy(false);
    if (res.diagnosis) setResult(res.diagnosis);
    else setErr(res.message || 'Test could not run.');
  }

  const ok = result?.ok;
  return (
    <div style={{
      marginTop: 16, padding: '14px 16px', borderRadius: 12,
      border: `1px solid ${ok === true ? '#bbf7d0' : ok === false ? '#fecaca' : '#e5e7eb'}`,
      background: ok === true ? '#f0fdf4' : ok === false ? '#fff7f7' : '#fafafa',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button
          onClick={run}
          disabled={busy}
          style={{
            border: 'none', borderRadius: 9, padding: '9px 15px', cursor: busy ? 'wait' : 'pointer',
            fontSize: 13, fontWeight: 800, color: '#fff', background: '#6f4ff0', opacity: busy ? 0.7 : 1,
          }}
        >
          {busy ? 'Testing Zana AI…' : '⚡ Test connection'}
        </button>
        <span style={{ fontSize: 12.5, color: '#6b7280' }}>
          Pings the server and makes one real Zana AI call.
        </span>
      </div>

      {err && <div style={{ marginTop: 10, fontSize: 13, color: '#b91c1c' }}>{err}</div>}

      {result && (
        <div style={{ marginTop: 12, fontSize: 13, lineHeight: 1.6 }}>
          <div style={{ fontWeight: 800, color: ok ? '#166534' : '#991b1b' }}>
            {ok ? '✓ Zana AI is connected and answering.' : '✗ Zana AI is not live — I am on offline rules.'}
          </div>
          {ok && result.sample && (
            <div style={{ color: '#374151', marginTop: 4 }}>Zana AI replied: “{result.sample}”</div>
          )}
          {!ok && result.error && (
            <div style={{ color: '#7f1d1d', marginTop: 4 }}><b>Reason:</b> {result.error}{result.httpStatus ? ` (HTTP ${result.httpStatus})` : ''}</div>
          )}
          <div style={{ color: '#9ca3af', marginTop: 8, fontSize: 12 }}>
            AI_ENABLED: <b>{String(result.enabledFlag)}</b> · provider: <b>{result.provider}</b> · model: <b>{result.model}</b>
            {' · '}key: <b>{result.hasApiKey ? (result.apiKeyPrefix || 'set') : 'MISSING'}</b>
            {result.killSwitchOn ? ' · kill-switch: ON' : ''}
          </div>
        </div>
      )}
    </div>
  );
}

// ── small presentational helpers ──────────────────────────────────────────
function ZKpi({ label, value, note, delta, accent, warn, good }: { label: string; value: string; note?: string; delta?: number; accent?: boolean; warn?: boolean; good?: boolean }) {
  return (
    <div className={`zana-kpi${accent ? ' accent' : ''}${warn ? ' warn' : ''}${good ? ' good' : ''}`}>
      <div className="zana-kpi-label">{label}</div>
      <div className="zana-kpi-value">{value}</div>
      {note && <div className="zana-kpi-note">{delta != null && delta !== 0 ? `${delta > 0 ? '▲' : '▼'}${Math.abs(delta)} · ` : ''}{note}</div>}
    </div>
  );
}

function ZCard({ title, note, pill, iconBg, iconFg, children }: { title: string; note?: string; pill?: string; iconBg: string; iconFg: string; children: React.ReactNode }) {
  return (
    <div className="zana-card">
      <div className="zana-card-head">
        <span className="zana-card-icon" style={{ background: iconBg, color: iconFg }}>◆</span>
        <div>
          <div className="zana-card-title">{title}</div>
          {note && <div className="zana-card-note">{note}</div>}
        </div>
        {pill && <span className="zana-ai-pill">{pill}</span>}
      </div>
      {children}
    </div>
  );
}

function SevPill({ sev }: { sev: 'high' | 'review' | 'low' }) {
  const s = SEV_STYLE[sev];
  return <span className="zana-sev" style={{ background: s.bg, color: s.fg }}>{s.label}</span>;
}
function ConfPill({ conf }: { conf: 'high' | 'medium' | 'low' }) {
  const s = CONF_STYLE[conf];
  return <span className="zana-sev" style={{ background: s.bg, color: s.fg }}>{conf}</span>;
}

const PILLAR_COLOR: Record<string, string> = {
  safety: '#dc2626',
  money: '#16a34a',
  parents: '#2563eb',
  setup: '#6f4ff0',
};

function MissionStrip({
  missions,
  onExecuteIntent,
}: {
  missions?: Briefing['missions'];
  onExecuteIntent?: ZanaIntentExecutor;
}) {
  const list = asList<any>(missions).slice(0, 2);
  if (!list.length) return null;
  return (
    <div className="zana-missions">
      <div className="zana-missions-head">This week&apos;s missions</div>
      <div className="zana-missions-grid">
        {list.map((m) => {
          const intents: ZanaIntent[] = m.actionId
            ? [{ actionId: m.actionId, params: m.params || {} }]
            : [];
          return (
            <div key={m.id} className="zana-mission-card">
              <div className="zana-mission-pillar" style={{ color: PILLAR_COLOR[m.pillar] || PURPLE }}>
                {(m.pillar || 'setup').toUpperCase()}
              </div>
              <div className="zana-mission-title">{m.title}</div>
              <div className="zana-mission-why">{m.why}</div>
              <div className="zana-mission-next"><b>Do this —</b> {m.nextStep}</div>
              {onExecuteIntent && intents.length > 0 && (
                <ZanaActionChips
                  intents={intents}
                  onExecuteIntent={onExecuteIntent}
                  tone="light"
                  onResult={(line) => customToast.show(line, 'success')}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
